import { Drawer } from "@tvg/fuse/drawer";
import { capitaliseFirstChar } from "common/utils/capitaliseFirstChar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMainState } from "../../../state/Main";
import { submitSearch, trackSearchEvent } from "../../../utils/searchUtils";
import styles from "./MobileSearch.module.css";

interface MobileSearchSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileSearchSidepanel: React.FC<MobileSearchSidepanelProps> = ({
  isOpen,
  onClose,
}) => {
  const brand = useMainState((state) => state.brand);
  const { searchTerm, setSearchTerm } = useMainState();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const placeholderText = `Search ${capitaliseFirstChar(brand === "littlewoods" ? brand : "Very")}`;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchTerm && searchTerm.trim().length > 0) {
        // Track search event
        trackSearchEvent("search_submit", "Real Estate", "Header", "Search");

        submitSearch(searchTerm.trim());
      }
    },
    [searchTerm],
  );

  const handleClose = useCallback(() => {
    // Track close event
    trackSearchEvent("search_close", "Real Estate", "Header", "Search");

    onClose();
  }, [onClose]);

  // Focus input when sidepanel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      if (
        document.activeElement &&
        document.activeElement !== inputRef.current
      ) {
        (document.activeElement as HTMLElement).blur();
      }

      const searchAnchor = document.querySelector(
        '[data-testid="mobile-header-search"]',
      );
      if (searchAnchor && document.activeElement === searchAnchor) {
        (searchAnchor as HTMLElement).blur();
      }

      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      };

      // Try to focus immediately
      requestAnimationFrame(focusInput);

      // Also try after a small delay as backup
      const timer = setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle focus and blur for placeholder text
  const handleFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  return (
    <Drawer
      className={`${styles.mobileSearch__drawer} navi-header-fe-mobile-search-drawer`}
      title="Search"
      open={isOpen}
      closeCallback={handleClose}
      dataTestId="mobile-search-drawer"
      desktopDisplay={false}
      slidePosition="left"
    >
      {isOpen && (
        <div className={styles.mobileSearch__searchContainer}>
          <form
            className={styles.mobileSearch__searchForm}
            onSubmit={handleSubmit}
            role="search"
          >
            <input
              ref={inputRef}
              className={styles.mobileSearch__searchInput}
              type="search"
              name="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isInputFocused ? "" : placeholderText}
              aria-label={placeholderText}
              data-testid="mobile-search-input"
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <button
              className={styles.mobileSearch__searchButton}
              type="submit"
              aria-label="Search"
              data-testid="complex-header-search-button"
              disabled={!searchTerm || searchTerm.trim().length === 0}
            >
              <div className={styles.mobileSearch__searchIcon} />
            </button>
          </form>
        </div>
      )}
    </Drawer>
  );
};

export default MobileSearchSidepanel;
